import { memo, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { ESIInclude } from "../../../__shared__/ESIInclude/ESIInclude";
import styles from "./SocialProofMessages.module.scss";
import { Experiences } from "@/getServerSideProps/monetate/getExperiences/experiencesSchema";
import { encodeExperiencesObject } from "@/utils/monetate/encodeExperiencesObject";
import { recordImpressionByParam } from "@JohnLewisPartnership/experiments-utils";
import cookies from "js-cookie";
import { reduceSPMPersistence } from "@/components/Galleries/utils.ts";
import { useMonetateId } from "@/hooks/useMonetateId/useMonetateId.ts";
import { transformExperiences } from "@/utils/monetate/transformExperiences";

export const SocialProofMessages = memo(function SocialProofMessages({
  productId,
  brand,
  availableToOrder,
  appId,
  experiencesRawObject = [],
  withClose = true,
  className = "",
  showSocialProofMessages = true,
  monetateEnvironment = "",
  reduceSocialProofMessagesPersistence = false,
}: {
  productId: string;
  brand: string;
  availableToOrder: boolean;
  appId: string;
  withClose?: boolean;
  className?: string;
  experiencesRawObject: Experiences;
  showSocialProofMessages?: boolean;
  monetateEnvironment?: string;
  reduceSocialProofMessagesPersistence?: boolean;
}) {
  const { monetateId } = useMonetateId();
  const [showESI, setShowESI] = useState(false);
  const filteredExperiences = experiencesRawObject?.filter(
    ({ parameters }) => parameters?.fragment === "social-proof-messaging",
  );

  const url = useRef(
    `/esi/social-proof-messaging-ui/?${new URLSearchParams({
      productId,
      brand,
      appId,
      availableToOrder: availableToOrder ? "true" : "false",
      withClose: withClose ? "true" : "false",
      ...(filteredExperiences.length > 0
        ? {
            experiences: encodeExperiencesObject(filteredExperiences),
          }
        : {}),
    }).toString()}`,
  );
  const clickCountRef = useRef(0);
  const sendManagedImpressionEvent = () => {
    recordImpressionByParam(
      monetateEnvironment,
      transformExperiences(experiencesRawObject),
      "pdp-scaffold-showSocialProofMessages",
      showSocialProofMessages ? "true" : "false",
      monetateId,
    );
  };

  useEffect(() => {
    const isSocialProofClosed = cookies.get("socialProofClosed") === "true";
    const id = `jl-social-proof-messaging-${productId}`;

    const targetNode = document.getElementById(id);

    if (!targetNode) {
      return;
    }

    // TODO: SPIKE SPDP-2930
    if (reduceSocialProofMessagesPersistence && isSocialProofClosed) {
      setShowESI(false);
      return;
    }

    const config = { childList: true, subtree: true };

    const callback = (mutationList: MutationRecord[]) => {
      for (const mutation of mutationList) {
        if (mutation.type === "childList") {
          if (document.querySelector('[data-testid="social-proof-messages"]')) {
            if (showSocialProofMessages) {
              setShowESI(true);
            }

            sendManagedImpressionEvent();
          }
        }
      }
    };

    const observer = new MutationObserver(callback);

    observer.observe(targetNode, config);

    return () => {
      observer.disconnect();
    };
  }, []);

  // TODO: SPIKE SPDP-2930
  useEffect(() => {
    if (!reduceSocialProofMessagesPersistence) return;

    const handleClick = () => {
      reduceSPMPersistence();
      setShowESI(false);
    };

    let intervalId: NodeJS.Timeout | null = null;
    let button: HTMLButtonElement | undefined = undefined;

    intervalId = setInterval(() => {
      const buttons = document.querySelectorAll<HTMLButtonElement>(
        "[data-testid='social-proof-messages'] button",
      );
      if (buttons?.length > 0) {
        button = buttons[0] ?? undefined;
        button?.addEventListener("click", handleClick);

        if (intervalId) clearInterval(intervalId);
      }
    }, 20);

    return () => {
      if (button) button?.removeEventListener("click", handleClick);
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (!reduceSocialProofMessagesPersistence) return;

    const button = document.querySelector<HTMLButtonElement>(
      "[data-testid='standard:gallery:navigation:right']",
    );
    if (!button) return;

    const handleClick = () => {
      clickCountRef.current += 1;
      if (clickCountRef.current === 2) {
        reduceSPMPersistence();
        setShowESI(false);
      }
    };

    button.addEventListener("click", handleClick);

    return () => {
      button.removeEventListener("click", handleClick);
    };
  }, []);

  return (
    productId &&
    brand &&
    appId && (
      <div
        data-testid={`social-proof-messaging-${productId}`}
        id={`jl-social-proof-messaging-${productId}`}
        className={classNames(
          "esi-wrapper",
          styles["social-proof-messages-esi-wrapper"],
          className && styles[className],
        )}
        style={{ display: showESI ? "unset" : "none" }}
      >
        <ESIInclude
          id={`jl-social-proof-messaging-${productId}`}
          src={url.current}
        />
      </div>
    )
  );
});
